import React, { useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  Modal,
  TextField,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import { Field, Formik } from "formik";
import * as yup from "yup";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { makeStyles } from "@mui/styles";
import { tokens } from "../../theme";
import useAuth from "../../hooks/useAuth";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useState } from "react";
import { customBaseUrl } from "../../action/https";

const IssueNewTerminalModal = ({ modal, setModal }) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { success, snackError, cookies } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const [isLoading, setIsLoading] = useState(false);
  const [approvedMerchant, setApprovedMerchant] = useState([]);
  const [approvedTerminal, setApprovedTerminal] = useState([]);

  const handleFormSubmit = async (values) => {
    setIsLoading(true);
    console.log(values);
    const { 
      serialNumber, 
      merchant, 
      level, 
      location, 
      maxTransactionAmount, 
      dailyTransactionLimit, 
      notes 
    } = values;
    const { terminalSerialNumber } = serialNumber;
    const resolvedRequestId = merchant.requestId || merchant.id;
    const resolvedUserId = merchant.userId || merchant.merchantId;

    try {
      const response = await axiosPrivate.post(
        `${customBaseUrl.posUrl}/pos-terminal/api/v1/terminal/issue`,
        {
          requestId: resolvedRequestId,
          terminalSerialNumber,
          userId: Number(resolvedUserId),
          level,
          location,
          notes: notes || undefined,
          maxTransactionAmount: maxTransactionAmount ? Number(maxTransactionAmount) : undefined,
          dailyTransactionLimit: dailyTransactionLimit ? Number(dailyTransactionLimit) : undefined,
        }
      );
      if (response?.data?.success || response?.data?.respCode === 0 ){
        success(response?.data?.message || response?.data?.respDesc);
      } else {
        snackError(response?.data?.message || response?.data?.respBody);
      }
      handleClose();
    } catch (error) {
      if (error.response) {
        snackError(error.response?.data?.message || error.response?.data?.respBody || error.response?.respBody);
      }
      console.log(error);
    }
    setIsLoading(false);
  };

  const getApprovedMerchant = async () => {
    try {
      const response = await axiosPrivate.get(
        `${customBaseUrl.posUrl}/pos-terminal/api/v1/terminal/approved-not-issued`
      );
      const resData = response?.data;
      let merchantList = resData?.data?.content || resData?.data || resData?.respBody?.content || resData?.respBody || resData?.content;
      if (merchantList && !Array.isArray(merchantList) && Array.isArray(merchantList.content)) {
        merchantList = merchantList.content;
      }
      setApprovedMerchant(Array.isArray(merchantList) ? merchantList : []);
    } catch (error) {
      console.log(error);
      setApprovedMerchant([]);
    }
  };

  const getApprovedTerminal = async (selectedMerchant) => {
    const terminalName = selectedMerchant?.terminalName || 
                         selectedMerchant?.terminalCategory || 
                         selectedMerchant?.terminalTypes?.[0]?.terminalName;
    if (!terminalName) return;
    try {
      const response = await axiosPrivate.get(
        `${customBaseUrl.posUrl}/pos-terminal/api/v1/terminal/unassigned/${terminalName}`
      );
      const resData = response?.data;
      let terminalList = resData?.data || resData?.respBody || resData;
      if (terminalList && !Array.isArray(terminalList) && Array.isArray(terminalList.content)) {
        terminalList = terminalList.content;
      }
      if (Array.isArray(terminalList)) {
        setApprovedTerminal(terminalList);
      } else {
        setApprovedTerminal([]);
        snackError(resData?.message || "No unassigned terminals found");
      }
    } catch (error) {
      if (error.response) {
        snackError(error.response?.data?.message || error.response?.data?.respBody || error.response?.respBody);
      }
      setApprovedTerminal([]);
      console.log(error);
    }
  };

  useEffect(() => {
    getApprovedMerchant();
  }, []);

  const initialValues = {
    merchant: null,
    serialNumber: null,
    terminalCost: "",
    amountPaid: "",
    actualTerminalName: "",
    preferredTerminalName: "",
    level: "",
    location: "",
    maxTransactionAmount: "",
    dailyTransactionLimit: "",
    notes: "",
  };

  const checkoutSchema = yup.object().shape({
    merchant: yup.object().required("Required"),
    serialNumber: yup.object().required("Required"),
    terminalCost: yup.number().required("Required"),
    amountPaid: yup.number(),
    actualTerminalName: yup.string().required("Required"),
    preferredTerminalName: yup.string().required("Required"),
    level: yup.string().required("Required"),
    location: yup.string().required("Required"),
    maxTransactionAmount: yup.number(),
    dailyTransactionLimit: yup.number(),
    notes: yup.string(),
  });

  const handleClose = () => {
    setModal((prev) => ({ ...prev, open: false }));
  };

  const handleTerminalSelectChange = (event, setFieldValue) => {
    const val = event.target.value;
    const selected = approvedTerminal.find(t => t.terminalSerialNumber === val);
    if (selected) {
      setFieldValue("serialNumber", selected);
      setFieldValue("terminalCost", selected.terminalAmount || selected.amount || selected.terminalCost || 0);
      setFieldValue("amountPaid", selected.amountPaid ? selected.amountPaid : 0);
      setFieldValue("actualTerminalName", selected.actualTerminalName || selected.terminalName || "");
      setFieldValue("preferredTerminalName", selected.preferredTerminalName || selected.terminalName || "");
    }
  };

  const handleSelectChange = (event, setFieldValue) => {
    const val = event.target.value;
    const selected = approvedMerchant.find(m => (m.requestId || m.merchantId) === val);
    setFieldValue("merchant", selected);
    getApprovedTerminal(selected);
  };

  return (
    <Modal open={modal.open} onClose={handleClose}>
      <Box
        sx={{
          width: "50%",
          backgroundColor: colors.primary[400],
          p: 2,
          position: "relative",
          top: "50%",
          left: "55%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={checkoutSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box padding={5}>
                <Typography
                  variant="h3"
                  sx={{ textAlign: "center", marginBottom: "30px" }}
                >
                  Issue a new POS Terminal
                </Typography>
                <Box
                  display="grid"
                  gap="20px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": {
                      gridColumn: isNonMobile ? undefined : "span 4",
                    },
                    marginBottom: "10px",
                  }}
                >
                  <TextField
                    label="Select Merchant"
                    fullWidth
                    select
                    onBlur={handleBlur}
                    onChange={(e) => handleSelectChange(e, setFieldValue)}
                    value={values.merchant ? (values.merchant.requestId || values.merchant.merchantId) : ""}
                    name="merchant"
                    error={!!touched.merchant && !!errors.merchant}
                    sx={{ gridColumn: "span 2" }}
                  >
                    {approvedMerchant?.map((merchant, index) => (
                      <MenuItem key={index} value={merchant.requestId || merchant.merchantId}>
                        {merchant?.merchantName || merchant?.requestedBy || merchant?.merchantId || merchant?.requestId || `Merchant #${index}`}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Serial Number"
                    fullWidth
                    select
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleTerminalSelectChange(e, setFieldValue);
                    }}
                    value={values.serialNumber ? values.serialNumber.terminalSerialNumber : ""}
                    name="serialNumber"
                    error={!!touched.serialNumber && !!errors.serialNumber}
                    sx={{ gridColumn: "span 2" }}
                  >
                    {approvedTerminal?.map((terminal, index) => (
                      <MenuItem key={index} value={terminal.terminalSerialNumber}>
                        {terminal?.terminalSerialNumber} - {terminal?.actualTerminalName || terminal?.terminalName}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    variant="filled"
                    disabled
                    type="number"
                    label="Terminal Cost"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.terminalCost}
                    name="terminalCost"
                    error={!!touched.terminalCost && !!errors.terminalCost}
                    helperText={touched.terminalCost && errors.terminalCost}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    disabled
                    type="number"
                    label="Amount Paid"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.amountPaid}
                    name="amountPaid"
                    error={!!touched.amountPaid && !!errors.amountPaid}
                    helperText={touched.amountPaid && errors.amountPaid}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Actual Terminal Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.actualTerminalName}
                    name="actualTerminalName"
                    error={
                      !!touched.actualTerminalName &&
                      !!errors.actualTerminalName
                    }
                    helperText={
                      touched.actualTerminalName && errors.actualTerminalName
                    }
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Preferred Terminal Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.preferredTerminalName}
                    name="preferredTerminalName"
                    error={
                      !!touched.preferredTerminalName &&
                      !!errors.preferredTerminalName
                    }
                    helperText={
                      touched.preferredTerminalName &&
                      errors.preferredTerminalName
                    }
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Level"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.level}
                    name="level"
                    error={!!touched.level && !!errors.level}
                    helperText={touched.level && errors.level}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Location"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.location}
                    name="location"
                    error={!!touched.location && !!errors.location}
                    helperText={touched.location && errors.location}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Max Transaction Amount"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.maxTransactionAmount}
                    name="maxTransactionAmount"
                    error={!!touched.maxTransactionAmount && !!errors.maxTransactionAmount}
                    helperText={touched.maxTransactionAmount && errors.maxTransactionAmount}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Daily Transaction Limit"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.dailyTransactionLimit}
                    name="dailyTransactionLimit"
                    error={!!touched.dailyTransactionLimit && !!errors.dailyTransactionLimit}
                    helperText={touched.dailyTransactionLimit && errors.dailyTransactionLimit}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Notes"
                    multiline
                    rows={3}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.notes}
                    name="notes"
                    error={!!touched.notes && !!errors.notes}
                    helperText={touched.notes && errors.notes}
                    sx={{ gridColumn: "span 4" }}
                  />
                </Box>
                <Button
                  type="submit"
                  color="secondary"
                  disabled={isLoading}
                  variant="contained"
                  sx={{ width: "100%", p: 2, mt: 3 }}
                >
                  Add Terminal
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default IssueNewTerminalModal;
